// Hierarchical breadcrumbs for the authed shell. Pure path → crumb list (no React).
import { paths } from '@/routes/paths';

export interface Crumb {
  label: string;
  to: string;
}

const RESEARCH: Crumb = { label: 'Research', to: paths.processPreference };
const PE: Crumb = { label: 'Private Equity', to: paths.pe.firms };
const IB: Crumb = { label: 'IB', to: paths.ib.banks };
const PREV_RESULTS: Crumb = { label: 'Previous Results', to: paths.previousResults };

const SETTINGS_TABS: Record<string, string> = {
  profile: 'Profile',
  'api-keys': 'API Keys',
  appearance: 'Appearance',
};

const ORG_PAGES: Record<string, string> = {
  dashboard: 'Dashboard',
  edit: 'Edit',
  billing: 'Billing',
  members: 'Members',
  invites: 'Invites',
  'credit-ledger': 'Credit Ledger',
  updates: 'Updates',
  crm: 'CRM',
  domains: 'Domains',
};

const ADMIN_PAGES: Record<string, string> = {
  ops: 'Admin Operations',
  'org-ops': 'Org Ops',
  'pending-registrations': 'Pending Registrations',
  'organization-approvals': 'Organization Approvals',
  'organization-updates': 'Organization Updates',
  organizations: 'Organizations',
  'provider-budgets': 'Provider Budgets',
};

const PE_PAGES: { path: string; label: string }[] = [
  { path: paths.pe.holdings, label: 'Holdings' },
  { path: paths.pe.people, label: 'People' },
  { path: paths.pe.reviewQueue, label: 'Review Queue' },
  { path: paths.pe.corrections, label: 'Corrections' },
  { path: paths.pe.screener, label: 'Screener' },
  { path: paths.pe.exitWatch, label: 'Exit Watch' },
  { path: paths.pe.changes, label: 'Activity' },
  { path: paths.pe.analysis, label: 'Analysis' },
  { path: paths.pe.talentFlow, label: 'Talent Flow' },
  { path: paths.pe.digest, label: 'Digest' },
  { path: paths.pe.marketMap, label: 'Market Map' },
  { path: paths.pe.ask, label: 'Ask the Market' },
  { path: paths.pe.tearsheet, label: 'Tearsheet' },
  { path: paths.pe.news, label: 'Market News' },
  { path: paths.pe.dataTools, label: 'Data Tools' },
  { path: paths.pe.actionQueue, label: 'Action Queue' },
  { path: paths.pe.admin, label: 'Admin' },
];

function isPath(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

const PROCESSED_TITLE_RE = /^processed results\b/i;

const LIST_PREFIXES = new Set([
  'target list',
  'strategic buyer list',
  'strategic buyers list',
  'financials buyer list',
  'financial buyer list',
  'financial verticals',
]);

function isListPrefix(part: string): boolean {
  const normalised = part.trim().toLowerCase();
  if (LIST_PREFIXES.has(normalised)) return true;
  return /^(target|strategic|financials?)\b.*\b(list|verticals)$/i.test(normalised);
}

function isDatePart(part: string): boolean {
  return /^(?:\d{1,2}[-/.]\d{1,2}(?:[-/.]\d{2,4})?|\d{4}[-/.]\d{1,2}[-/.]\d{1,2})$/.test(
    part.trim(),
  );
}

export interface ResultDisplayName {
  /** Mandate/core name for breadcrumbs. Omitted until a real name exists. */
  crumb?: string;
  /** Page heading, e.g. "Result - b2b saas companies". */
  heading: string;
}

/**
 * Stored result names look like `Target list - b2b saas companies - karan - 17-09.xlsx`.
 * Strip the list-type prefix and the trailing username + date.
 * Financial Verticals runs have no filename — fall back to industry / target description.
 */
export function fvMandateName(filters?: unknown): string | undefined {
  if (!filters || typeof filters !== 'object') return undefined;
  const f = filters as Record<string, unknown>;
  const industry = String(f.industry ?? '').trim();
  const sub = String(f.subIndustry ?? f.sub_industry ?? '').trim();
  if (industry && sub) return `${industry} / ${sub}`;
  if (industry) return industry;
  const desc = String(f.targetDescription ?? f.target_description ?? '').trim();
  if (!desc) return undefined;
  const first = desc.split(/[.!?\n]/)[0].trim();
  if (!first) return undefined;
  if (first.length <= 72) return first;
  return `${first.slice(0, 69).trimEnd()}…`;
}

export function parseResultDisplayName(
  title?: string | null,
  filename?: string | null,
  filters?: unknown,
): ResultDisplayName {
  const fromFile = filename?.replace(/\.[^.]+$/, '').trim() ?? '';
  const titleTrim = title?.trim() ?? '';
  const titleUsable = titleTrim && !PROCESSED_TITLE_RE.test(titleTrim) ? titleTrim : '';
  const raw = fromFile || titleUsable;
  if (!raw) {
    const mandate = fvMandateName(filters);
    if (!mandate) return { heading: 'Result' };
    return { crumb: mandate, heading: `Result - ${mandate}` };
  }

  const parts = raw
    .split(/\s+-\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return { heading: 'Result' };

  if (isListPrefix(parts[0])) parts.shift();
  if (parts.length >= 2 && isDatePart(parts[parts.length - 1])) {
    parts.pop();
    if (parts.length >= 2) parts.pop();
  }

  const core = parts.join(' - ').trim();
  if (!core) {
    const mandate = fvMandateName(filters);
    if (!mandate) return { heading: 'Result' };
    return { crumb: mandate, heading: `Result - ${mandate}` };
  }
  return { crumb: core, heading: `Result - ${core}` };
}

/** Breadcrumb label for a result — the core mandate name, or nothing until it exists. */
export function resultCrumbName(
  title?: string | null,
  filename?: string | null,
  filters?: unknown,
): string | undefined {
  return parseResultDisplayName(title, filename, filters).crumb;
}

/** Results page heading, e.g. "Result - b2b saas companies". */
export function resultHeadingName(
  title?: string | null,
  filename?: string | null,
  filters?: unknown,
): string {
  return parseResultDisplayName(title, filename, filters).heading;
}

function withEntity(base: Crumb[], entityName: string | undefined, to: string): Crumb[] {
  const label = entityName?.trim();
  if (!label) return base;
  return [...base, { label, to }];
}

/**
 * Crumbs for the current authed path. The last crumb is the current page (still a link).
 * Landing (`/`) returns an empty trail. Entity crumbs are omitted until a real name exists.
 */
export function buildBreadcrumbs(
  pathname: string,
  search = '',
  entityName?: string | null,
): Crumb[] {
  const crumbs = match(pathname, search, entityName?.trim() || undefined);
  if (crumbs.length === 0) return crumbs;
  const last = crumbs[crumbs.length - 1];
  const lastPath = last.to.split('?')[0];
  if (lastPath === pathname) {
    crumbs[crumbs.length - 1] = { ...last, to: `${pathname}${search}` };
  }
  return crumbs;
}

function match(pathname: string, search: string, entityName?: string): Crumb[] {
  if (pathname === paths.landing || pathname === '') return [];

  // Result workspace — always Previous Results, even when opened from a list/composer.
  if (
    isPath(pathname, '/quralyst-research/result') ||
    isPath(pathname, '/financial-verticals/results')
  ) {
    return withEntity([RESEARCH, PREV_RESULTS], entityName, pathname);
  }

  if (isPath(pathname, paths.previousResults)) {
    const tab = new URLSearchParams(search).get('active_tab');
    if (tab === 'tearsheets') {
      return [
        RESEARCH,
        PREV_RESULTS,
        { label: 'Tearsheets', to: paths.previousResultsTab('tearsheets') },
      ];
    }
    return [RESEARCH, PREV_RESULTS];
  }

  if (pathname === paths.processPreference) {
    return [RESEARCH, { label: 'Homepage', to: paths.processPreference }];
  }
  if (pathname === paths.targetList) {
    return [RESEARCH, { label: 'Target List', to: paths.targetList }];
  }
  if (pathname === paths.strategic) {
    return [RESEARCH, { label: 'Strategic Research', to: paths.strategic }];
  }
  if (pathname === paths.financialVerticalsDatabase) {
    return [
      RESEARCH,
      { label: 'Financial Verticals', to: paths.financialVerticals },
      { label: 'Database', to: paths.financialVerticalsDatabase },
    ];
  }
  if (pathname === paths.financialVerticalsFirmMemory) {
    return [
      RESEARCH,
      { label: 'Financial Verticals', to: paths.financialVerticals },
      { label: 'Firm Memory', to: paths.financialVerticalsFirmMemory },
    ];
  }
  if (pathname === paths.financialVerticals) {
    return [RESEARCH, { label: 'Financial Verticals', to: paths.financialVerticals }];
  }
  if (pathname === paths.knowledgeBank) {
    return [RESEARCH, { label: 'Knowledge Bank', to: paths.knowledgeBank }];
  }
  if (pathname === paths.analystPreferences) {
    return [RESEARCH, { label: 'My Preferences', to: paths.analystPreferences }];
  }
  if (pathname === paths.emailSync) {
    return [RESEARCH, { label: 'Email Sync', to: paths.emailSync }];
  }
  if (pathname === paths.usage) {
    return [RESEARCH, { label: 'API Usage', to: paths.usage }];
  }
  if (pathname === paths.scorecard) {
    return [RESEARCH, { label: 'Scorecard', to: paths.scorecard }];
  }
  if (pathname === paths.bdScoring) {
    return [RESEARCH, { label: 'BD Scoring', to: paths.bdScoring }];
  }

  // Shared bottom-block pages — no Research / PE prefix.
  if (isPath(pathname, '/settings')) {
    const tab = pathname.split('/')[2] ?? '';
    const tabLabel = SETTINGS_TABS[tab] ?? 'Settings';
    return [
      { label: 'Settings', to: paths.settings.apiKeys },
      { label: tabLabel, to: pathname },
    ];
  }
  if (pathname === paths.faq) {
    return [{ label: 'FAQs', to: paths.faq }];
  }
  if (pathname === paths.deals) {
    return [{ label: 'Deals', to: paths.deals }];
  }
  if (isPath(pathname, paths.deals)) {
    return withEntity([{ label: 'Deals', to: paths.deals }], entityName, pathname);
  }

  const orgMatch = pathname.match(/^\/org\/([^/]+)(?:\/([^/]+))?$/);
  if (orgMatch) {
    const slug = orgMatch[1];
    const page = orgMatch[2] ?? 'dashboard';
    return [
      { label: 'Organization', to: paths.org.dashboard(slug) },
      { label: ORG_PAGES[page] ?? page, to: pathname },
    ];
  }

  if (isPath(pathname, '/admin')) {
    const rest = pathname.slice('/admin/'.length);
    const adminHome: Crumb = { label: 'Admin Ops', to: paths.admin.adminOps };
    if (!rest || rest === 'ops') return [adminHome];
    return [adminHome, { label: ADMIN_PAGES[rest] ?? rest, to: pathname }];
  }

  // Saved tearsheets live on Previous Results — keep the Research trail when opening one.
  if (isPath(pathname, '/pe/tearsheet/view') || pathname === paths.pe.tearsheet) {
    const query = new URLSearchParams(search);
    const company = query.get('company')?.trim() || entityName?.trim();
    const hasSheet = Boolean(
      company || query.get('id')?.trim() || isPath(pathname, '/pe/tearsheet/view'),
    );
    if (hasSheet) {
      return withEntity(
        [RESEARCH, { label: 'Previous Results', to: paths.previousResultsTab('tearsheets') }],
        company,
        pathname,
      );
    }
    return [PE, { label: 'Tearsheet', to: paths.pe.tearsheet }];
  }

  // PE
  if (isPath(pathname, paths.pe.firms) && pathname !== paths.pe.firms) {
    return withEntity([PE, { label: 'Firms', to: paths.pe.firms }], entityName, pathname);
  }
  if (pathname === paths.pe.firms) {
    return [PE, { label: 'Firms', to: paths.pe.firms }];
  }
  for (const page of PE_PAGES) {
    if (isPath(pathname, page.path)) {
      return [PE, { label: page.label, to: page.path }];
    }
  }

  // IB
  if (pathname === paths.ib.banks) {
    return [IB, { label: 'Banks', to: paths.ib.banks }];
  }
  if (pathname === paths.ib.transactions) {
    return [IB, { label: 'Transactions', to: paths.ib.transactions }];
  }
  if (pathname === paths.ib.people) {
    return [IB, { label: 'Professionals', to: paths.ib.people }];
  }
  if (pathname === paths.ib.screener) {
    return [IB, { label: 'IB Screener', to: paths.ib.screener }];
  }
  if (isPath(pathname, '/ib')) {
    return withEntity([IB, { label: 'Banks', to: paths.ib.banks }], entityName, pathname);
  }

  if (pathname === paths.comingSoon) {
    return [{ label: 'Coming Soon', to: paths.comingSoon }];
  }
  if (pathname === paths.pricing) {
    return [{ label: 'Pricing', to: paths.pricing }];
  }

  return [];
}
